import type { ConditionGroup, SectionDef, TemplateDefinition } from '../forms/types.js';
import { ACCESS_STATUS_OPTIONS, NO_PASSWORDS_NOTICE, SectionBuilder, type OptionSpec } from './builder.js';
import { buildC01Section, C01_CONFIRMATION } from './c01.js';

/** F04 — Social Media Optimization Onboarding (11 numbered sections + C01). */

const has = (field: string, values: string[]): ConditionGroup => ({ all: [{ field, op: 'contains_any', value: values }] });

function sec(n: number, key: string, title: string) {
  const nn = String(n).padStart(2, '0');
  return new SectionBuilder(`f04_s${nn}`, { number: `F04.${nn}`, key, title });
}

/** Option keys are normalized provider keys; labels keep the source wording. */
const PLATFORMS: Array<{ key: string; label: string }> = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'x_twitter', label: 'X/Twitter' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'other', label: 'Other' },
];
const PLATFORM_OPTIONS: OptionSpec[] = PLATFORMS.map((p) => (p.key === 'other' ? { ...p, opensDetail: true } : p));

/** Stable ids of the F04.03 platforms field, referenced from F04.10. */
const PLATFORMS_FIELD_ID = 'f04_s03_platforms';
const platformOptionId = (key: string) => `${PLATFORMS_FIELD_ID}__${key}`;

function s01(): SectionDef {
  const sb = sec(1, 'business', 'Business Information');
  sb.add('company_brand', 'short_text', 'Company/Brand', { required: true, profileKey: 'public_brand' });
  sb.add('legal_name', 'short_text', 'Legal Name', { profileKey: 'legal_name' });
  sb.add('industry', 'short_text', 'Industry/Niche', { required: true, profileKey: 'industry' });
  sb.add('website', 'url', 'Website', { profileKey: 'website' });
  sb.add('primary_contact', 'short_text', 'Primary Contact', { required: true, profileKey: 'primary_contact_name' });
  sb.add('designation', 'short_text', 'Designation', { profileKey: 'primary_contact_designation' });
  sb.add('email', 'email', 'Email', { required: true, profileKey: 'email' });
  sb.add('phone', 'phone', 'Phone/WhatsApp', { required: true, profileKey: 'phone' });
  return sb.build();
}

function s02(): SectionDef {
  const sb = sec(2, 'business_goals', 'Business Overview & Social Goals');
  sb.add('business_overview', 'long_text', 'Business Overview', { required: true, profileKey: 'business_description' });
  sb.add('products_services', 'long_text', 'Products/Services');
  sb.add('target_audience', 'long_text', 'Target Audience/Personas');
  sb.add('target_locations', 'long_text', 'Target Locations/Countries');
  sb.add('social_goals', 'multi_select', 'Social Media Goals', {
    required: true,
    options: [
      'Brand awareness',
      'Engagement',
      'Lead generation',
      'Sales',
      'Community growth',
      'Website traffic',
      'Recruitment',
      'Customer support',
    ],
  });
  sb.add('primary_social_objective', 'short_text', 'Primary Social Objective', { required: true });
  sb.add('kpis_targets', 'long_text', 'KPIs/Targets');
  return sb.build();
}

function s03(): SectionDef {
  const sb = sec(3, 'platforms', 'Social Platforms & Profiles');
  const f = sb.add('platforms', 'multi_select', 'Platforms', { required: true, options: PLATFORM_OPTIONS });
  if (f.id !== PLATFORMS_FIELD_ID) throw new Error('F04 platforms field id drifted');
  sb.info(
    'profiles_vs_access',
    'Profiles and access grants are separate: listing a profile here does not grant access. Admin access is tracked per platform below and requested in F04.10.',
    'Profiles and access',
  );
  for (const p of PLATFORMS) {
    const vis = has(PLATFORMS_FIELD_ID, [platformOptionId(p.key)]);
    const g = `${p.label} — `;
    sb.add(`${p.key}_profile_url`, 'url', `${g}Platform/profile URL`, { visibleWhen: vis, requiredWhen: vis, accessProvider: p.key });
    sb.add(`${p.key}_handle`, 'short_text', `${g}Handle`, { visibleWhen: vis });
    sb.add(`${p.key}_followers`, 'integer', `${g}Followers`, { visibleWhen: vis, validation: { min: 0 } });
    sb.add(`${p.key}_followers_as_of`, 'date', `${g}Followers as-of date`, { visibleWhen: vis, dateConstraint: 'past' });
    sb.add(`${p.key}_admin_access`, 'single_select', `${g}Admin access`, {
      visibleWhen: vis,
      accessProvider: p.key,
      options: ACCESS_STATUS_OPTIONS,
      help: 'Pending/Granted',
    });
  }
  return sb.build();
}

function s04(): SectionDef {
  const sb = sec(4, 'current_performance', 'Current Performance');
  sb.info('self_reported', 'Performance information is self-reported unless an analytics integration exists.', 'Self-reported');
  sb.add('performance_summary', 'long_text', 'Current performance summary');
  sb.add('best_posts', 'long_text', 'Best posts');
  sb.add('analytics_reports', 'file_upload', 'Analytics/reports', { assetCategory: 'reports' });
  sb.add('analytics_reports_link', 'asset_link', 'Analytics/reports link', { assetCategory: 'reports' });
  return sb.build();
}

function s05(): SectionDef {
  const sb = sec(5, 'brand_voice_assets', 'Brand Voice & Assets');
  sb.add('tone', 'multi_select', 'Tone', {
    options: [
      { label: 'Formal/professional', key: 'formal_professional' },
      'Friendly',
      'Educational',
      'Conversational',
      'Premium',
      'Humorous',
      'Technical',
      { label: 'Other', key: 'other', opensDetail: true },
    ],
  });
  sb.add('voice_tone_notes', 'long_text', 'Voice/tone notes');
  sb.add('words_phrases', 'long_text', 'Words/phrases to use/avoid');
  const assets: Array<[string, string, string]> = [
    ['logo', 'Logo', 'logo'],
    ['brand_guidelines', 'Brand Guidelines', 'brand_guidelines'],
    ['colors', 'Colors', 'colors'],
    ['fonts', 'Fonts', 'fonts'],
    ['images', 'Images', 'imagery'],
    ['videos', 'Videos', 'video'],
    ['existing_creatives', 'Existing Creatives', 'existing_creatives'],
    ['templates', 'Templates', 'templates'],
  ];
  for (const [key, label, category] of assets) {
    sb.add(`${key}_provided`, 'yes_no', label, { assetCategory: category, help: 'Provided? (Yes/No)' });
  }
  sb.add('reference_files', 'file_upload', 'Links/reference files', { assetCategory: 'brand_kit' });
  sb.add('reference_link', 'asset_link', 'Reference folder link', { assetCategory: 'brand_kit' });
  return sb.build();
}

function s06(): SectionDef {
  const sb = sec(6, 'content_pillars', 'Content Pillars');
  sb.add('content_types', 'multi_select', 'Content Types', {
    options: [
      'Educational',
      { label: 'Product/service', key: 'product_service' },
      'Behind the scenes',
      'Testimonials',
      'Case studies',
      { label: 'Tips/how-to', key: 'tips_how_to' },
      'Industry news',
      { label: 'Founder/expert', key: 'founder_expert' },
      'Promotional',
      { label: 'UGC', key: 'ugc' },
    ],
  });
  sb.add('categories_themes', 'long_text', 'Categories/themes');
  sb.add('topics_claims_avoid', 'long_text', 'Topics/claims to avoid');
  sb.add('reference_brands', 'long_text', 'Reference brands');
  return sb.build();
}

function s07(): SectionDef {
  const sb = sec(7, 'competitors', 'Competitors');
  const r = 'competitors';
  sb.add(r, 'repeater', 'Competitors', {
    rowLabel: 'Competitor',
    validation: { maxRows: 50 },
    children: [
      sb.child(r, 'competitor', 'short_text', 'Competitor', { required: true }),
      sb.child(r, 'platform', 'single_select', 'Platform', { options: PLATFORM_OPTIONS }),
      sb.child(r, 'profile_url', 'url', 'Profile URL'),
      sb.child(r, 'like_dislike', 'long_text', 'What you like/dislike'),
    ],
  });
  return sb.build();
}

function s08(): SectionDef {
  const sb = sec(8, 'posting_campaigns', 'Posting Frequency & Campaigns');
  sb.info('no_publishing', 'This is onboarding information only; this platform does not publish posts.', 'Onboarding information only');
  sb.add('posting_frequency', 'single_select', 'Posting frequency', {
    options: [
      'Daily',
      { label: '3-5/week', key: 'per_week_3_5' },
      { label: '2-3/week', key: 'per_week_2_3' },
      'Weekly',
      { label: 'Custom', key: 'custom', opensDetail: true },
    ],
  });
  sb.add('posting_schedule', 'long_text', 'Posting schedule');
  sb.add('upcoming_campaigns', 'long_text', 'Upcoming campaigns');
  sb.add('offers_promos', 'long_text', 'Offers/promos/seasonal');
  sb.add('objectives_deliverables', 'long_text', 'Objectives/deliverables');
  return sb.build();
}

function s09(): SectionDef {
  const sb = sec(9, 'cta_leads', 'Calls to Action & Lead Handling');
  sb.info(
    'whatsapp_consent',
    'Selecting WhatsApp as a CTA is not consent to receive WhatsApp messages.',
    'WhatsApp',
  );
  sb.add('cta_options', 'multi_select', 'Calls to Action', {
    options: [
      'Website',
      'Call',
      { label: 'WhatsApp', key: 'whatsapp' },
      { label: 'DM', key: 'dm' },
      'Lead form',
      'Booking',
      'Purchase',
      'Newsletter',
    ],
  });
  sb.add('preferred_ctas', 'long_text', 'Preferred CTAs');
  sb.add('lead_handling', 'long_text', 'Lead handling');
  sb.add('sales_contact', 'short_text', 'Sales contact');
  sb.add('landing_urls', 'long_text', 'Landing URLs');
  return sb.build();
}

function s10(): SectionDef {
  const sb = sec(10, 'access', 'Platform Access');
  sb.info('no_passwords', NO_PASSWORDS_NOTICE, 'Access instructions');
  sb.info(
    'access_dedupe',
    'Access requests are de-duplicated: the same resource, role and invitee is requested only once. The items below follow the platforms selected in F04.03.',
    'Access requests',
  );
  const item = (key: string, label: string, provider: string, platformKeys: string[] | null, roleHint: string) => {
    const vis = platformKeys ? has(PLATFORMS_FIELD_ID, platformKeys.map(platformOptionId)) : undefined;
    const v = vis ? { visibleWhen: vis } : {};
    sb.add(`${key}_status`, 'single_select', label, {
      ...v,
      options: ACCESS_STATUS_OPTIONS,
      accessProvider: provider,
      help: `Access status (Pending/Granted). Appropriate role: ${roleHint}.`,
    });
    sb.add(`${key}_invitee`, 'email', `${label}: Access Email/Invitee`, {
      ...v,
      accessProvider: provider,
      requiredWhen: { all: [{ field: sb.id(`${key}_status`), op: 'equals', value: sb.optionId(`${key}_status`, 'granted') }] },
    });
    sb.add(`${key}_role`, 'short_text', `${label}: Appropriate Role`, { ...v, accessProvider: provider, placeholder: roleHint });
  };
  item('meta', 'Instagram/Meta Business Suite', 'meta_business_suite', ['instagram'], 'Partner/Admin or Content role as required');
  item('facebook', 'Facebook Page', 'facebook', ['facebook'], 'Page role with required tasks');
  item('linkedin', 'LinkedIn Page', 'linkedin', ['linkedin'], 'Content Admin or Analyst');
  item('youtube', 'YouTube', 'youtube', ['youtube'], 'Manager or Editor');
  const xVis = has(PLATFORMS_FIELD_ID, ['x_twitter', 'tiktok', 'other'].map(platformOptionId));
  sb.add('x_tiktok_other_platform', 'single_select', 'X/TikTok/Other: Platform', {
    visibleWhen: xVis,
    accessProvider: 'other',
    options: [
      { label: 'X/Twitter', key: 'x_twitter' },
      { label: 'TikTok', key: 'tiktok' },
      { label: 'Other', key: 'other', opensDetail: true },
    ],
  });
  item('x_tiktok_other', 'X/TikTok/Other', 'other', ['x_twitter', 'tiktok', 'other'], 'Appropriate delegated role');
  item('analytics', 'Analytics/Reporting', 'analytics', null, 'Read-only/reporting role');
  return sb.build();
}

function s11(): SectionDef {
  const sb = sec(11, 'approvals_reporting', 'Approvals & Reporting');
  sb.add('content_approval_person', 'short_text', 'Content approval person', { required: true });
  sb.add('final_decision_maker', 'short_text', 'Final decision maker');
  sb.add('approval_turnaround', 'short_text', 'Expected approval turnaround');
  sb.add('reporting', 'multi_select', 'Reporting', {
    options: ['Weekly', 'Monthly', 'Campaign', { label: 'Platform-wise', key: 'platform_wise' }, { label: 'Custom', key: 'custom', opensDetail: true }],
  });
  sb.add('reporting_kpis', 'long_text', 'KPIs');
  sb.add('reporting_format', 'short_text', 'Format');
  sb.add('reporting_notes', 'long_text', 'Notes');
  return sb.build();
}

export const F04_SOCIAL: TemplateDefinition = {
  key: 'social_media_onboarding',
  code: 'F04',
  name: 'Social Media Optimization Onboarding',
  description: 'Onboarding questionnaire for social media optimization engagements.',
  serviceModule: 'social',
  sections: [s01(), s02(), s03(), s04(), s05(), s06(), s07(), s08(), s09(), s10(), s11(), buildC01Section('f04')],
  confirmation: C01_CONFIRMATION,
};
